import {
  faCircleXmark,
  faMagnifyingGlass,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Circle } from "rc-progress";
import { Doughnut, Line, Bar } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Dropdown,
  FloatingLabel,
  FormControl,
  Row,
} from "react-bootstrap";
import { DatePicker, Select } from "antd";
import TotalSum from "../../statsComponents/TotalSum";
import StaffStats from "../../statsComponents/StaffStats";
import { useDispatch, useSelector } from "react-redux";
import {
  buildLast12MonthBuckets,
  calculateBellmanStats,
  calculateDockRevenue,
  calculateIndividualTipStats,
  calculateMaidStats,
  calculateRefundedTransactionStats,
  calculateTeamTipStats,
  calculateTipsCollected,
  calculateTotalCommissionCharged,
  calculateTotalStripeFee,
  calculateTotalSubcharged,
  calculateTotalSum,
  calculateValetStats,
  countByClient,
  getDailyData,
  getMonthlyData,
  getUnverifiedTeamTips,
  getVerifiedTeamTips,
  getWeeklyData,
  sumByClient,
} from "../../controllers/stats";
import Loader from "../../components/Loader";
import { getAllStaff, getAllValetTeams } from "../../redux/Actions/maidAction";
import { getAllPaymentReports } from "../../redux/Actions/paymentAction";
import DateRangeFilter from "../../statsComponents/DateRangeFilter";
import { listUsers } from "../../redux/slices/clientSlice";

Chart.register(...registerables);
const Stats = () => {
  const { RangePicker } = DatePicker;
  const { Option } = Select;
  const [query, setQuery] = useState("");
  const [errorMessage, setErrorMessage] = useState(false);
  const [fliterApplied, setFilterApplied] = useState(false);
  const [reset, setReset] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [monthlyData, setMonthlyData] = useState({});
  const [weeklyData, setWeeklyData] = useState({});
  const [doughnutData, setDoughnutData] = useState(null);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [staff, setStaff] = useState([]);
  const [valetTeams, setValetTeams] = useState([]);
  const [showDailyChart, setShowDailyChart] = useState(false);
  const [dailyData, setDailyData] = useState({});
  const [topClientsBar, setTopClientsBar] = useState(null);

  const { loading: transactionsLoading, allPayments: allPyaments } =
    useSelector((state) => state.paymentReports);

  const {
    staff: staffData,
    loading: staffLoading,
    error: staffError,
  } = useSelector((state) => state.getAllStaff);

  const {
    valetTeams: valetTeamsData,
    loading: valetTeamsLoading,
    error: valetTeamsError,
  } = useSelector((state) => state.getAllValetTeams);

  const { listLoading: loading, clients } = useSelector(
    (state) => state.client
  );

  const dispatch = useDispatch();

  const handleInputChange = (e) => {
    const input = e.target.value;
    setSelectedUserId(null);
    setQuery(input);
    setErrorMessage(false);

    if (input.trim().length > 0) {
      const matches = clients.filter((user) =>
        user.companyName.toLowerCase().includes(input.toLowerCase())
      );
      setFilteredUsers(matches);
      setShowDropdown(matches.length > 0);
    } else {
      setFilteredUsers([]);
      setShowDropdown(false);
    }
  };
  const handleDateRangeChange = (dates) => {
    if (dates === null || dates[0] === null || dates[1] === null) {
      setErrorMessage(false);
      setDateRange([null, null]);
    } else {
      setDateRange(dates);
    }
  };

  const handleSelectUser = (user) => {
    setQuery(user.companyName);
    setSelectedUserId(user._id);
    setShowDropdown(false);
  };

  useEffect(() => {
    if (!allPyaments.length) dispatch(getAllPaymentReports());
    if (!valetTeamsData.length) dispatch(getAllValetTeams());
    if (!clients.length) dispatch(listUsers());
    if (!staffData.length) dispatch(getAllStaff());
  }, []);

  useEffect(() => {
    if (staffData?.length > 0) {
      setStaff(staffData);
    }
    if (valetTeamsData?.length > 0) {
      setValetTeams(valetTeamsData);
    }
  }, [staffData, valetTeamsData]);

  useEffect(() => {
    const filteredData = fliterApplied ? filteredTransactions : allPyaments;
    // 📌 Set Monthly Data
    const { data: monthlyCounts, years: monthYears } =
      getMonthlyData(filteredData);
    const monthlyLabels = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ].map((month, index) => `${month} ${monthYears[index]}`); // Adds year dynamically

    const monthlyChartData = {
      labels: monthlyLabels,
      datasets: [
        {
          label: "Transactions",
          data: monthlyCounts,
          backgroundColor: "rgba(75,192,192,0.4)", // Teal
          borderColor: "rgba(75,192,192,1)",
          borderWidth: 2,
          fill: true,
        },
      ],
    };
    setMonthlyData(monthlyChartData);
    // 📌 If Date Range is Applied → Show Daily Chart
    if (dateRange[0] && dateRange[1]) {
      setShowDailyChart(true);
      const dailyChartData = getDailyData(
        filteredData,
        dateRange[0],
        dateRange[1]
      );

      setDailyData({
        labels: dailyChartData.labels,
        datasets: [
          {
            label: "Transactions Per Day",
            data: dailyChartData.data,
            borderColor: "rgba(75, 192, 192, 1)", // Teal
            backgroundColor: "rgba(75, 192, 192, 0.2)", // Teal (transparent)
            borderWidth: 2,
            fill: true,
          },
        ],
      });
    } else {
      // 📌 No Date Range → Show Weekly Chart
      setShowDailyChart(false);

      let targetMonth = new Date().getMonth();
      let targetYear = new Date().getFullYear();
      const weeklyDataCounts = getWeeklyData(
        filteredData,
        targetMonth,
        targetYear
      );
      const weeklyChartData = {
        labels: Array.from({ length: 31 }, (_, i) => i + 1),
        datasets: [
          {
            label: "Week 1",
            data: weeklyDataCounts.datasets[0].data,
            borderColor: "rgba(75, 192, 192, 1)", // Teal
            backgroundColor: "rgba(75, 192, 192, 0.2)", // Teal (transparent)
            borderWidth: 2,
            fill: true,
          },
          {
            label: "Week 2",
            data: weeklyDataCounts.datasets[1].data,
            borderColor: "rgba(153, 102, 255, 1)", // Purple
            backgroundColor: "rgba(153, 102, 255, 0.2)", // Purple (transparent)
            borderWidth: 2,
            fill: true,
          },
          {
            label: "Week 3",
            data: weeklyDataCounts.datasets[2].data,
            borderColor: "rgba(255, 159, 64, 1)", // Orange
            backgroundColor: "rgba(255, 159, 64, 0.2)", // Orange (transparent)
            borderWidth: 2,
            fill: true,
          },
          {
            label: "Week 4",
            data: weeklyDataCounts.datasets[3].data,
            borderColor: "rgba(255, 99, 132, 1)", // Red
            backgroundColor: "rgba(255, 99, 132, 0.2)", // Red (transparent)
            borderWidth: 2,
            fill: true,
          },
        ],
      };
      setWeeklyData(weeklyChartData);
    }
    // 📌 Set Doughnut Chart Data for Verified & Unverified Team Tips
    if (valetTeams) {
      const verifiedTeamTipsCount = getVerifiedTeamTips(valetTeams);
      const unverifiedTeamTipsCount = getUnverifiedTeamTips(valetTeams);

      setDoughnutData({
        labels: ["Verified Tips", "Unverified Tips"],
        datasets: [
          {
            data: [verifiedTeamTipsCount, unverifiedTeamTipsCount],
            backgroundColor: ["#36A2EB", "#FF6384"],
            hoverBackgroundColor: ["#36A2EB", "#FF6384"],
          },
        ],
      });
    }

    // -------------------------- TOP 5 CLIENTS BAR (by total tips) --------------------------
    (() => {
      let baseTxns = [...allPyaments];

      if (dateRange[0] && dateRange[1]) {
        const start = new Date(dateRange[0]);
        const end = new Date(dateRange[1]);
        end.setHours(23, 59, 59, 999);
        baseTxns = baseTxns.filter((t) => {
          const d = new Date(t.createdAt);
          return d >= start && d <= end;
        });
      }

      //  count transactions (tips) per client
      const countsMap = countByClient(baseTxns);

      // Top 5 by count
      const topPairs = Array.from(countsMap.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

      if (topPairs.length === 0) {
        setTopClientsBar(null);
        return;
      }

      const labels = topPairs.map(([id]) => {
        const client = (clients || []).find((c) => c._id === id);
        return client?.companyName || "Unknown";
      });

      const data = topPairs.map(([, count]) => count);

      console.log("Clients graph data: ", labels, data);

      setTopClientsBar({
        labels,
        datasets: [
          {
            label: "Number of tips",
            data,
            backgroundColor: "rgba(52,152,219,0.7)",
            borderColor: "rgba(52,152,219,1)",
            borderWidth: 1,
            borderRadius: 4,
            categoryPercentage: 0.9,
            barPercentage: 0.9,
            barThickness: 25,
            maxBarThickness: 24,
            minBarLength: 4,
          },
        ],
      });
    })();
  }, [fliterApplied, filteredTransactions, allPyaments, dateRange, valetTeams]);

  const applyFilters = useCallback(() => {
    let transactions = [...allPyaments];
    let filteredStaff = [...staffData];
    let filteredTeams = [...valetTeamsData];

    if (selectedUserId) {
      setFilterApplied(true);
      transactions = transactions.filter(
        (txn) => txn.clientId === selectedUserId
      );
      filteredStaff = filteredStaff.filter(
        (user) => user.hotel === selectedUserId
      );
      filteredTeams = filteredTeams.filter(
        (team) => team.hotel === selectedUserId
      );
    }

    if (dateRange[0] && dateRange[1]) {
      setFilterApplied(true);
      const start = new Date(dateRange[0]);
      const end = new Date(dateRange[1]);
      //  Extend `endDate` to include the entire last day
      end.setHours(23, 59, 59, 999);

      transactions = transactions.filter((txn) => {
        const txnDate = new Date(txn.createdAt);
        return txnDate >= start && txnDate <= end;
      });

      filteredStaff = filteredStaff.filter((user) => {
        const userCreated = new Date(user.createdAt);
        return userCreated >= start && userCreated <= end;
      });

      filteredTeams = filteredTeams.filter((team) => {
        const teamCreated = new Date(team.createdAt);
        return teamCreated >= start && teamCreated <= end;
      });
    }

    setFilteredTransactions(transactions);
    setStaff(filteredStaff);
    setValetTeams(filteredTeams);
  }, [selectedUserId, dateRange, allPyaments, staffData, valetTeamsData]);

  useEffect(() => {
    applyFilters();
  }, [selectedUserId, dateRange]);

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
      },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#ffffff",
        bodyColor: "#e0e0e0",
        padding: 10,
        displayColors: false,
        callbacks: {
          title: (tooltipItems) => `📅 ${tooltipItems[0].label}`,
          label: (tooltipItem) => `🔥 Transactions: ${tooltipItem.raw}`,
        },
      },
    },
  };
  const donoughtOptions = {
    plugins: {
      tooltip: {
        enabled: true,
        padding: 12,
        titleFont: {
          size: 16, // Bigger title font
          weight: "bold",
        },
        bodyFont: {
          size: 14, // Bigger body font
        },
        boxPadding: 10, // Adds padding around dataset color box
      },
    },
  };
  const barOptions = {
    responsive: true,
    indexAxis: "y",
    plugins: {
      legend: {
        display: true,
      },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        titleColor: "#ffffff",
        bodyColor: "#e0e0e0",
        padding: 10,
        callbacks: {
          label: (ctx) => ` ${Number(ctx.raw || 0).toLocaleString()} tips`,
        },
      },
    },
    scales: {
      x: {
        ticks: {
          precision: 0,
          color: "#aaa",
        },
        beginAtZero: true,
        grid: { color: "rgba(0, 0, 0, 0.05)" },
      },
      y: {
        ticks: {
          color: "#ccc",
        },
        grid: { color: "rgba(255,255,255,0.05)" },
      },
    },
  };

  const styles = {
    shadow: {
      boxShadow: " rgba(100, 100, 111, 0.1) 0px 7px 29px 0px",
    },
  };

  return (
    <>
      <Row className="m-0 py-4">
        <Col>
          {/* ------------------------------------------------ FILTERS ---------------------------------------------- */}
          <Row className="align-items-center justify-content-center mb-4 p-2 rounded-2 bg-light">
            <Col xs="auto" className="">
              <h3 className="mb-0 text-primary fs-5 ">Perfomance Overview</h3>
            </Col>
            <Col xs={3} className="ms-auto">
              <RangePicker
                className="rounded-2 py-1 border-0 w-100"
                value={dateRange}
                onChange={handleDateRangeChange}
              />
            </Col>
            <Col xs="auto" className="p-0">
              <DateRangeFilter setDateRange={setDateRange} />
            </Col>
            <Col xs="auto">
              <div className="position-relative">
                <FormControl
                  className="rounded-2 py-1 border-0 text-muted"
                  type="text"
                  value={query}
                  onChange={handleInputChange}
                  placeholder="Client name"
                />
                <span
                  className="position-absolute top-50 end-0 translate-middle-y me-2 text-secondary"
                  style={{ cursor: "pointer" }}
                >
                  {query || showDropdown ? (
                    <FontAwesomeIcon
                      icon={faCircleXmark}
                      size="sm"
                      onClick={() => {
                        setQuery("");
                        setSelectedUserId(null);
                        setShowDropdown(false);
                        // setFilterApplied(false);
                      }}
                    />
                  ) : (
                    <FontAwesomeIcon icon={faMagnifyingGlass} size="sm" />
                  )}
                </span>
              </div>
              {showDropdown && (
                <Dropdown show>
                  <Dropdown.Menu
                    className="rounded-4 mt-1"
                    style={{ width: "100%" }}
                  >
                    {filteredUsers.map((user, index) => (
                      <Dropdown.Item
                        key={index}
                        onClick={() => handleSelectUser(user)}
                      >
                        {user.companyName}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </Col>
          </Row>
          {/* ================================================================ TRANSACTION RECORDS ========================================================== */}
          {transactionsLoading ? (
            <Row>
              <Loader />
              <h6 className="text-center my-1 fw-normal">
                Loading Transaction Data
              </h6>
            </Row>
          ) : (
            <Row className="border border-0 px-5 pt-3 my-4">
              <Col md={3} className="text-center">
                <h5 className="fw-normal text-center">Total Transactions</h5>
                <span className="fw-normal text-primary fs-2">
                  {(fliterApplied ? filteredTransactions : allPyaments).length}
                </span>
              </Col>
              <Col md={4} className="text-center">
                <h5 className="fw-normal text-center">Total Money Processed</h5>
                <span className="fw-normal text-success fs-2">
                  $
                  {calculateTotalSum(
                    fliterApplied ? filteredTransactions : allPyaments
                  )}
                </span>
              </Col>
              <Col md={4} className="ms-auto">
                <Row>
                  {/* ------------------------------------------ INDIVIDUAL TIPS RATIO ------------------------------------ */}
                  <Col md={4}>
                    <Row className="position-relative circle-container mx-auto">
                      <Circle
                        percent={
                          calculateIndividualTipStats(
                            fliterApplied ? filteredTransactions : allPyaments
                          ).percentageOfIndividualTips
                        }
                        strokeWidth={10}
                        trailWidth={8}
                        strokeColor={"#06BF9C"}
                      />
                      <div className="circle-text">
                        <span className="text-success">
                          {
                            calculateIndividualTipStats(
                              fliterApplied ? filteredTransactions : allPyaments
                            ).percentageOfIndividualTips
                          }
                          %
                        </span>
                      </div>
                    </Row>
                    <Row>
                      <span className="text-success text-center">
                        Direct tips{" "}
                        {
                          calculateIndividualTipStats(
                            fliterApplied ? filteredTransactions : allPyaments
                          ).numberOfIndividualTipTransactions
                        }
                      </span>
                    </Row>
                  </Col>
                  {/* ------------------------------------------ VALE POOL TIPS RATIO ------------------------------------ */}
                  <Col md={4}>
                    <Row className="position-relative circle-container mx-auto">
                      <Circle
                        percent={
                          calculateTeamTipStats(
                            fliterApplied ? filteredTransactions : allPyaments
                          ).percentageOfTeamTips
                        }
                        strokeWidth={10}
                        trailWidth={8}
                        strokeColor={"#06BF9C"}
                      />
                      <div className="circle-text">
                        <span className="text-success">
                          {
                            calculateTeamTipStats(
                              fliterApplied ? filteredTransactions : allPyaments
                            ).percentageOfTeamTips
                          }
                          %
                        </span>
                      </div>
                    </Row>
                    <Row>
                      <span className="text-success text-center">
                        Pool tips{" "}
                        {
                          calculateTeamTipStats(
                            fliterApplied ? filteredTransactions : allPyaments
                          ).numberOfTeamTipTransactions
                        }
                      </span>
                    </Row>
                  </Col>
                  {/* ------------------------------------------ REFUNDED RATIO ------------------------------------ */}
                  <Col md={4}>
                    <Row className="position-relative circle-container mx-auto">
                      <Circle
                        percent={
                          calculateRefundedTransactionStats(
                            fliterApplied ? filteredTransactions : allPyaments
                          ).percentageOfRefundedTransactions
                        }
                        strokeWidth={10}
                        trailWidth={8}
                        strokeColor={"#3498db"}
                      />
                      <div className="circle-text">
                        <span className="text-info">
                          {
                            calculateRefundedTransactionStats(
                              fliterApplied ? filteredTransactions : allPyaments
                            ).percentageOfRefundedTransactions
                          }
                          %
                        </span>
                      </div>
                    </Row>
                    <Row>
                      <span className="text-info text-center">
                        Refunded{" "}
                        {
                          calculateRefundedTransactionStats(
                            fliterApplied ? filteredTransactions : allPyaments
                          ).numberOfRefundedTransactions
                        }
                      </span>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
          {/* ================================================================ PAYMENT BREAKDOWN ============================================================ */}
          <Row className="mb-4 px-4 justify-content-between">
            <TotalSum
              title={"Total of Tip amounts"}
              col={3}
              amount={calculateTipsCollected(
                fliterApplied ? filteredTransactions : allPyaments
              )}
            />
            <TotalSum
              title={"Total Subcharged"}
              amount={calculateTotalSubcharged(
                fliterApplied ? filteredTransactions : allPyaments
              )}
            />
            <TotalSum
              title={"Total Stripe Fee Paid"}
              amount={calculateTotalStripeFee(
                fliterApplied ? filteredTransactions : allPyaments
              )}
            />
            <TotalSum
              title={"Commission Charged"}
              amount={calculateTotalCommissionCharged(
                fliterApplied ? filteredTransactions : allPyaments
              )}
            />
            <TotalSum
              title={"Dock's Revenue"}
              amount={calculateDockRevenue(
                fliterApplied ? filteredTransactions : allPyaments
              )}
            />
          </Row>
          {/* ===================================== MONTHLY & WEELY & CLIENTS GRAPHS ============================ */}
          <Row className="">
            <Col md={12} className="p-3">
              <Row className="row-gap-2">
                <Col xs={6}>
                  <h5 className="text-center">Monthly Tipping Activity</h5>
                  {monthlyData?.datasets?.length > 0 && (
                    <Line data={monthlyData} options={options} />
                  )}
                </Col>
                <Col xs={6}>
                  <h5 className="text-center">
                    {showDailyChart
                      ? "Daily Tipping Activity"
                      : "Weekly Tipping Activity"}{" "}
                    {showDailyChart && (
                      <span className="fs-7 text-muted">(Filtered Dates)</span>
                    )}
                  </h5>
                  {showDailyChart && dailyData?.datasets?.length > 0 ? (
                    <Line data={dailyData} options={options} />
                  ) : (
                    weeklyData?.datasets?.length > 0 && (
                      <Line data={weeklyData} options={options} />
                    )
                  )}
                </Col>
              </Row>
            </Col>
            <Col md={12} className="p-3">
              <Row className="row-gap-4">
                <Col xs={12}>
                  <h5 className="text-center">
                    Top 5 Clients Tipping Activity
                    {dateRange[0] && dateRange[1] ? (
                      <span className="fs-7 text-muted"> (Filtered Dates)</span>
                    ) : null}
                  </h5>
                  {topClientsBar && (
                    <Bar
                      data={topClientsBar}
                      options={barOptions}
                      height={70}
                    />
                  )}
                </Col>
              </Row>
            </Col>
          </Row>

          {/* ======================================== STAFF RECORDS ================================== */}

          <Row className="pt-4 justify-content-between align-items-center">
            <Col xs={7}>
              {staffLoading ? (
                <Row>
                  <Loader />
                  <h6 className="text-center my-1 fw-normal">
                    Loading Staff Data
                  </h6>
                </Row>
              ) : (
                <Row className="border border-0 rounded-3 p-3 pt-5 ">
                  <Col md={12} className="mb-3">
                    <h5 className=" fw-normal text-center">
                      Total Staff Onboarded
                    </h5>
                    <h2 className="fw-normal text-primary text-center">
                      {staff.length}
                    </h2>
                  </Col>
                  <Col md={12} className="ms-auto">
                    <Row>
                      {/* ------------------------------------------ ATTENDANTS RATIO ------------------------------------ */}
                      <Col xs={4}>
                        <Row className="position-relative circle-container mx-auto">
                          <Circle
                            percent={
                              calculateMaidStats(staff).percentageOfMaids
                            }
                            strokeWidth={10}
                            trailWidth={8}
                            strokeColor={"#004172"}
                          />
                          <div className="circle-text">
                            <span className="text-primary">
                              {calculateMaidStats(staff).percentageOfMaids}%
                            </span>
                          </div>
                        </Row>
                        <Row>
                          <span className="text-primary text-center text-break p-0">
                            Attendants {calculateMaidStats(staff).totalMaids}
                          </span>
                        </Row>
                      </Col>
                      {/* ------------------------------------------ VALET RATIO ------------------------------------ */}
                      <Col xs={4}>
                        <Row className="position-relative circle-container mx-auto">
                          <Circle
                            percent={
                              calculateValetStats(staff).percentageOfValets
                            }
                            strokeWidth={10}
                            trailWidth={8}
                            strokeColor={"#004172"}
                          />
                          <div className="circle-text">
                            <span className="text-primary">
                              {calculateValetStats(staff).percentageOfValets}%
                            </span>
                          </div>
                        </Row>
                        <Row>
                          <span className="text-primary text-center text-break p-0">
                            Valets {calculateValetStats(staff).totalValets}
                          </span>
                        </Row>
                      </Col>
                      {/* ------------------------------------------ BELLMEN RATIO ------------------------------------ */}
                      <Col xs={4}>
                        <Row className="position-relative circle-container mx-auto">
                          <Circle
                            percent={
                              calculateBellmanStats(staff).percentageOfBellmen
                            }
                            strokeWidth={10}
                            trailWidth={8}
                            strokeColor={"#004172"}
                          />
                          <div className="circle-text">
                            <span className="text-primary">
                              {calculateBellmanStats(staff).percentageOfBellmen}
                              %
                            </span>
                          </div>
                        </Row>
                        <Row>
                          <span className="text-primary text-center text-break p-0">
                            Bellmen {calculateBellmanStats(staff).totalBellmen}
                          </span>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              )}
              {/* <StaffStats
                title={"Attendants"}
                active={calculateMaidStats(staff).activeMaids}
                inActive={calculateMaidStats(staff).inactiveMaids}
              />

              <StaffStats
                title={"Valets"}
                active={calculateValetStats(staff).activeValets}
                inActive={calculateValetStats(staff).inactiveValets}
              />

              <StaffStats
                title={"Bell Staff"}
                active={calculateBellmanStats(staff).activeBellmen}
                inActive={calculateBellmanStats(staff).inactiveBellmen}
              /> */}
            </Col>
            {valetTeamsLoading ? (
              <Col xs={4}>
                <Row>
                  <Loader />
                  <h6 className="text-center my-1 fw-normal">
                    Loading team tips data
                  </h6>
                </Row>
              </Col>
            ) : (
              doughnutData && (
                <Col xs={4}>
                  <h5 className="text-center">Valet Pool Tips</h5>
                  <Doughnut data={doughnutData} options={donoughtOptions} />
                </Col>
              )
            )}
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default Stats;
